import { type ReactElement, type ReactNode } from 'react';
import { Link } from '@/components/link';
import SvgHome from '@/components/icons-eataly/SvgHome';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { EATALY_BREADCRUMB_DEFAULT_BACKGROUND_CLASS, type EatalyBreadcrumbItem } from './index';

const BREADCRUMB_SEPARATOR = '/';

type EatalyBreadcrumbsSkeletonShellProps = {
    backgroundClassName?: string;
    className?: string;
    'aria-label'?: string;
    'data-testid'?: string;
    children: ReactNode;
};

function EatalyBreadcrumbsSkeletonShell({
    backgroundClassName = EATALY_BREADCRUMB_DEFAULT_BACKGROUND_CLASS,
    className,
    'aria-label': ariaLabelProp,
    'data-testid': dataTestId = 'eataly-breadcrumbs-skeleton',
    children,
}: EatalyBreadcrumbsSkeletonShellProps): ReactElement {
    const { t } = useTranslation('category');
    const ariaLabel = ariaLabelProp ?? t('breadcrumbs.label');
    return (
        <nav
            aria-label={ariaLabel}
            aria-busy="true"
            className={cn(backgroundClassName, 'grid-global py-3', className)}
            data-testid={dataTestId}>
            <ol className="col-span-full flex min-h-0 flex-nowrap items-center gap-2 overflow-hidden lg:col-start-2 lg:flex-wrap lg:overflow-visible">
                {children}
            </ol>
        </nav>
    );
}

function EatalyBreadcrumbsHomeLink(): ReactElement {
    const { t } = useTranslation('category');
    return (
        <li className="flex min-w-0 items-center">
            <Link to="/" className="link-breadcrumb inline-flex shrink-0" aria-label={t('breadcrumbs.home')}>
                <SvgHome className="h-4 w-4" width={16} height={16} aria-hidden />
            </Link>
        </li>
    );
}

function EatalyBreadcrumbTailSkeleton({ className }: { className?: string }): ReactElement {
    return <Skeleton className={cn('h-4 w-36 max-w-[min(100%,12rem)]', className)} />;
}

/**
 * Full strip placeholder while a deferred PDP `breadcrumbItems` Promise resolves (direct PDP load).
 */
export function EatalyBreadcrumbsSkeleton({
    backgroundClassName = EATALY_BREADCRUMB_DEFAULT_BACKGROUND_CLASS,
}: {
    backgroundClassName?: string;
} = {}): ReactElement {
    return (
        <EatalyBreadcrumbsSkeletonShell backgroundClassName={backgroundClassName}>
            <EatalyBreadcrumbsHomeLink />
            <li className="flex shrink-0 items-center gap-2">
                <EatalyBreadcrumbTailSkeleton className="w-24" />
            </li>
            <li className="flex shrink-0 items-center gap-2">
                <span className="link-breadcrumb shrink-0 select-none" aria-hidden>
                    {BREADCRUMB_SEPARATOR}
                </span>
                <EatalyBreadcrumbTailSkeleton className="w-28" />
            </li>
            <li className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                <span className="link-breadcrumb shrink-0 select-none" aria-hidden>
                    {BREADCRUMB_SEPARATOR}
                </span>
                <EatalyBreadcrumbTailSkeleton />
            </li>
        </EatalyBreadcrumbsSkeletonShell>
    );
}

/**
 * Keeps the resolved PLP (or prior page) trail and appends a product-name skeleton (PLP → PDP navigation).
 */
export function EatalyBreadcrumbsStaleWithTailSkeleton({
    staleItems,
    backgroundClassName = EATALY_BREADCRUMB_DEFAULT_BACKGROUND_CLASS,
}: {
    staleItems: EatalyBreadcrumbItem[];
    backgroundClassName?: string;
}): ReactElement {
    return (
        <EatalyBreadcrumbsSkeletonShell
            backgroundClassName={backgroundClassName}
            data-testid="eataly-breadcrumbs-stale-tail-skeleton">
            <EatalyBreadcrumbsHomeLink />
            {staleItems.map((item, index) => {
                const key = item.id ?? `eataly-crumb-stale-${index}`;
                return (
                    <li key={key} className="flex shrink-0 items-center gap-2">
                        {index > 0 && (
                            <span className="link-breadcrumb shrink-0 select-none" aria-hidden>
                                {BREADCRUMB_SEPARATOR}
                            </span>
                        )}
                        {item.href ? (
                            <Link to={item.href} className="link-breadcrumb shrink-0 whitespace-nowrap">
                                {item.label}
                            </Link>
                        ) : (
                            <span className="link-breadcrumb shrink-0 whitespace-nowrap">{item.label}</span>
                        )}
                    </li>
                );
            })}
            <li className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                <span className="link-breadcrumb shrink-0 select-none" aria-hidden>
                    {BREADCRUMB_SEPARATOR}
                </span>
                <EatalyBreadcrumbTailSkeleton />
            </li>
        </EatalyBreadcrumbsSkeletonShell>
    );
}

/**
 * Suspense fallback for deferred `breadcrumbItems`: full skeleton, or stale trail + product tail skeleton.
 */
export function EatalyBreadcrumbsPendingFallback({
    staleItemsWhilePending,
    backgroundClassName = EATALY_BREADCRUMB_DEFAULT_BACKGROUND_CLASS,
}: {
    staleItemsWhilePending?: EatalyBreadcrumbItem[];
    backgroundClassName?: string;
}): ReactElement {
    if (staleItemsWhilePending != null && staleItemsWhilePending.length > 0) {
        return (
            <EatalyBreadcrumbsStaleWithTailSkeleton
                staleItems={staleItemsWhilePending}
                backgroundClassName={backgroundClassName}
            />
        );
    }
    return <EatalyBreadcrumbsSkeleton backgroundClassName={backgroundClassName} />;
}
